/**
 * Formats numeric values embedded in generated smart narratives (not axis ticks, raw DB fields, or calendar years).
 */
export const NARRATIVE_NO_DATA = '—';

export type NarrativeNumberOptions = {
  /** When true, positive values are prefixed with '+'. Minus signs are always kept. */
  signed?: boolean;
  /** Suffix such as '%', ' mi/day', ' min' — not auto-added; pass explicitly when needed. */
  suffix?: string;
  /** When true and |value| rounds to 0 with 2dp but value > 0, show "<0.01" instead of "0.00". */
  guardTinyPositive?: boolean;
};

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Decimal places from absolute magnitude (before rounding). */
export function narrativeDecimalPlaces(absValue: number): number {
  if (absValue < 10) return 2;
  if (absValue < 100) return 1;
  return 0;
}

function formatWithPlaces(value: number, minimumFractionDigits: number, maximumFractionDigits: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits,
    maximumFractionDigits,
  });
}

/**
 * Core narrative number formatter.
 * Examples: 5.678 → 5.68; 9.999 → 10.00; 15.678 → 15.7; 1234.56 → 1,235; -1234.56 → -1,235
 */
export function formatNarrativeNumber(
  value: number | null | undefined,
  options: NarrativeNumberOptions = {}
): string {
  if (!isFiniteNumber(value)) {
    return NARRATIVE_NO_DATA;
  }

  const abs = Math.abs(value);
  const places = narrativeDecimalPlaces(abs);

  if (options.guardTinyPositive !== false && value > 0 && abs < 0.005 && places === 2) {
    return `<0.01${options.suffix ?? ''}`;
  }

  const rounded = Number(value.toFixed(places));
  const formatted = formatWithPlaces(rounded, places, places);

  let out = formatted;
  if (options.signed && rounded > 0) {
    out = `+${formatted}`;
  }
  if (options.suffix) {
    out = `${out}${options.suffix}`;
  }
  return out;
}

/** Percent change or share for narrative text (value is already in percent units, e.g. 12.34 means 12.34%). */
export function formatNarrativePercent(
  value: number | null | undefined,
  options: Omit<NarrativeNumberOptions, 'suffix'> = {}
): string {
  return formatNarrativeNumber(value, { ...options, suffix: '%' });
}

/** Large mile counts: uses thousands grouping at |x| >= 100; smaller values keep narrative decimals. */
export function formatNarrativeMiles(value: number | null | undefined): string {
  if (!isFiniteNumber(value)) return NARRATIVE_NO_DATA;
  return `${formatNarrativeNumber(value)} mi`;
}

/** Millions of miles for compact total-VMT narrative (input is raw annual miles). */
export function formatNarrativeMillionMiles(annualMiles: number | null | undefined): string {
  if (!isFiniteNumber(annualMiles)) return NARRATIVE_NO_DATA;
  const millions = annualMiles / 1_000_000;
  return `${formatNarrativeNumber(millions)} million mi`;
}
